import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CategoryNode,
  LeadResource,
  Locale,
  PageDetail,
  PageSummary,
  SiteContext,
  StoredBlock,
} from '@icetus/contracts';
import {
  LOCALE_LABEL,
  MAX_BLOCKS_PER_PAGE,
  THEME_FIXED_PATHS,
  formatFileSize,
  highlightIdsOf,
  withLocalePrefix,
  type BlockHighlightTarget,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  createPages,
  createPreviewToken,
  deletePage,
  discardPreviewDraft,
  fetchBlocks,
  fetchCategories,
  fetchPage,
  fetchPages,
  fetchResources,
  fetchSites,
  saveBlocks,
  savePreviewDraft,
  updatePage,
  type BlockLibraryItem,
} from '../api';
import { BlockChevron } from '../components/block-chevron';
import { BlockPropsEditor, MissingFieldsNotice, type FieldChoices } from '../components/block-fields';
import { useConfirm } from '../components/confirm';
import { Drawer } from '../components/overlay';
import { PreviewPane } from '../components/preview-pane';
import { openBlankTab } from '../lib/blank-tab';
import { blockSummary } from '../lib/block-summary';
import { loadPhaseOf } from '../lib/load-phase';
import { siteOrigin as previewOrigin } from '../lib/site-url';

/**
 * 页面内容编辑。
 *
 * 表单不按区块类型手写，而是**遍历 props 生成输入框**。理由：
 * 内置区块有 10 个、字段几十个，手写一遍意味着每加一个字段就要改三处
 * （协议、渲染、后台表单），而第三处永远最容易漏。遍历的代价是复杂结构
 * （数组里的对象）排版不如定制表单好看，但不会漏字段。
 *
 * 保存走 PUT 整页替换，服务端会用 Block 协议重新校验一遍；校验失败会带
 * 具体到哪个区块、哪个字段的信息回来，这里就事论事地显示出来。
 *
 * 「添加区块」刻意**不单独落库**，而是把它并进本地 blocks 并标脏，等客户点保存
 * 一次性提交。否则先加块、再改文案，两步之间页面会被服务端刷新一次，
 * 客户正在编辑的那几个字就没了——这种丢失不会报错，只会让人觉得"白改了"。
 */

/** 区块分类的展示名。协议里的 category 是稳定标识，中文只活在视图层 */
const CATEGORY_LABEL: Record<BlockLibraryItem['category'], string> = {
  hero: '首屏',
  content: '内容',
  commerce: '商品',
  trust: '信任背书',
  contact: '联系',
};

const CATEGORY_ORDER: BlockLibraryItem['category'][] = [
  'hero',
  'commerce',
  'content',
  'trust',
  'contact',
];

/** 本地生成块 id。服务端会接受并沿用它；不用 crypto.randomUUID 是因为它在非安全上下文里没有 */
function newBlockId(): string {
  return `blk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 预览联动的防抖窗口。
 *
 * 太短（<300ms）会在打字时把 iframe 刷成频闪 —— 每次刷新都是一次整页重载，
 * 首屏的大图会跟着闪一遍。太长（>1.5s）又回到"我改了、它没动"的怀疑里。
 * 700ms 大约是"停下手"的时长：一个字打完停一下不会触发，一句话打完刚好。
 */
const PREVIEW_DRAFT_DEBOUNCE_MS = 700;

/**
 * 预览地址的协议。实现移到 lib/site-url.ts —— 商品列表的「看前台」
 * 也要判断同一件事，两处各写一份迟早会改漏一处。
 */

export function ContentPage({
  tenantId,
  canGenerateArt = false,
  onDirtyChange,
}: {
  tenantId: string;
  /**
   * 能不能用 AI 生图（按 `site:update` 派生，editor 没有）。
   *
   * 默认 false：漏传的代价是"看不到按钮"，而不是"点了一下被告知没权限"——
   * 后者会让客户以为功能坏了。
   */
  canGenerateArt?: boolean;
  /** 把"有没有未保存改动"报给外壳：侧栏切页面时也要拦一次 */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  /**
   * 站点列表是否还在读。
   *
   * 这是**第一个**要到的东西：没有站点就没有站点 id，下面两个请求都发不出去。
   * 不单独记这一态的话，`siteId` 还是空串的那几百毫秒里 `pages` 也必然是空的，
   * 而"空"在这一页会被读成"这个站点没有页面" —— 客户刚点进来先看见两段空态
   * 文案（页面还没有、区块还没有），一两秒后才换成真实内容。
   */
  const [sitesLoading, setSitesLoading] = useState(true);
  const [pages, setPages] = useState<PageSummary[]>([]);
  /**
   * 页面列表是否还在读。
   *
   * `pages` 的初值是 `[]`，而 `[]` 在这一页的含义是"这个站点一页都没有" ——
   * 两者撞在一起时，网络慢的客户会先看到「这个站点还没有页面」，几百毫秒后
   * 内容又自己冒出来。他截个图就能说"你们把内容弄丢了"。
   * 所以凡是"空 = 一种状态"的地方，都必须先区分"还没读到"与"真的没有"。
   */
  const [pagesLoading, setPagesLoading] = useState(true);
  /**
   * 当前页面的区块是否还在读。
   *
   * 比列表慢一步（列表到了才去取详情），中间右侧是整块空白 —— 客户已经看见
   * 页面列表里有 4 个页面，却看不到任何区块，第一反应是"这一页的内容没了"。
   */
  const [detailLoading, setDetailLoading] = useState(false);
  /**
   * `pages` 里那份列表属于哪个站点、`detail` 属于哪一页。
   *
   * 用来盖住"选中值已经变了、请求还没发出去"的那一帧：`siteId` 由站点请求的回执
   * 直接设好，而 `pagesLoading` 是在下一个 effect 里才被置 true —— 中间这一帧
   * `pages` 还是空的、loading 还是 false，空列表就被当成"这个站点没有页面"。
   * 比对 id 不看时序，切站点、切页面都不会闪一下空态。
   */
  const [pagesForSite, setPagesForSite] = useState('');
  const [detailForPage, setDetailForPage] = useState('');
  const [pageId, setPageId] = useState('');
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [blocks, setBlocks] = useState<StoredBlock[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * 新建页面表单。
   *
   * 四个状态放在一起、用同一个开关控制，是因为它们只在抽屉里活一次：
   * 建完就清空，失败时保留（客户改一个字再试，不该从头填）。
   */
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPath, setNewPath] = useState('');
  const [newCopyFrom, setNewCopyFrom] = useState('');
  const [library, setLibrary] = useState<BlockLibraryItem[]>([]);
  const [categories, setCategories] = useState<CategoryNode[]>([]);
  /**
   * 当前站点的资料列表，只用来给「资料下载」区块填下拉。
   *
   * 刻意和分类分开存：两者失败互不影响（分类拉失败还要能改标题，
   * 资料拉失败还要能改文案），合成一次请求会让一个 500 卡住整张表单。
   *
   * `null` = **还没拉到 / 拉失败**，与"确实一份都没有"（`[]`）不是一回事：
   * 前者要把字段退回文本输入，后者要提示客户去上传。
   */
  const [resources, setResources] = useState<LeadResource[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * 预览面板是否展开。**默认展开** —— 改区块的意义就是看右边有没有跟着变，
   * 每次进来先点一次「预览」是白花一步。
   *
   * 但只在放得下两列时才默认开：窄屏（≤1180px，与 styles.css 里那条媒体查询
   * 同一条线）预览会变成整高的右侧抽屉，默认展开等于一进页面编辑器就被盖住，
   * 客户第一反应是"怎么全挡住了"。那种宽度下仍然由客户自己点。
   */
  const [previewOpen, setPreviewOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1181px)').matches,
  );
  /** 自增即让预览面板重新取令牌并刷新 iframe。保存成功后推一下 */
  const [previewReload, setPreviewReload] = useState(0);
  /** 草稿最近一次同步成功的时刻。null = 预览里显示的是已保存内容 */
  const [draftSyncedAt, setDraftSyncedAt] = useState<Date | null>(null);
  /**
   * 当前编辑中的区块 —— 右侧预览据此把对应的那一段用虚线框出来。
   *
   * 存的是**算好的目标**，不是区块 id：`blocks` 每敲一个字就是一个新数组，
   * 由它派生出来的对象每帧都换引用，预览那边就会一遍遍收到"高亮这一段"，
   * 平滑滚动反复重放，按着键打字时像地震。这里只在获得焦点那一刻算一次。
   */
  const [active, setActive] = useState<{ blockId: string; target: BlockHighlightTarget } | null>(
    null,
  );
  /**
   * **展开**了的区块（注意：存的是展开，不是收起）。
   *
   * 默认全部收起 —— 一页十几段、每段十几个字段时，全展开要滚十几屏才看得到
   * 页面有哪几段。所以初始值是空集合（= 一个都没展开），客户要改哪段再点开。
   *
   * 存"展开"而不是"收起"就是为了让默认状态**天然**等于空集合：换一页时
   * 直接把集合清空即可，不用在数据回来那一刻去枚举所有区块 id 塞进去
   * （那时候 blocks 还是上一页的，按它算会漏掉新页面的区块）。
   *
   * 故意不持久化：换一页就该回到"只看目录"的视角。存进 localStorage 的话，
   * 客户明天进来会看到昨天展开到一半的样子，与这一页的实情无关。
   */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  /** 刚顶开、还需要滚进视口的那一段。用完立刻清空，见下面的 effect */
  const [revealTarget, setRevealTarget] = useState<string | null>(null);
  /**
   * 草稿请求的序号。并发时只认最后一次的回执：
   * 打字快的时候两个 PUT 可能乱序返回，按先发的时刻去刷新 iframe，
   * 预览就会停在**上一版**，而界面显示"已同步"。
   */
  const draftSeq = useRef(0);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  /**
   * 刷新/关标签页时也要拦。
   *
   * 同一件事在三个地方发生：切页面、切站点、切侧栏（那三处走确认框），
   * 以及**直接刷新浏览器**。少这一处的话，客户改了半天按一下 F5 就全没了 ——
   * 而且没有任何提示，他甚至不确定是自己改丢了还是本来就没保存。
   * 浏览器只允许弹一个不能自定义文案的框，这已经是能做到的全部。
   */
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // 旧浏览器（以及部分内嵌浏览器）只认 returnValue
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  /**
   * 切页面前拦截未保存的改动。
   *
   * 加区块、改文案都只落在本地，切页面/切站点时不会报错也不会提示，
   * 客户会以为已经改好了——这种丢失比报错难查得多，因为它没有痕迹。
   */
  const guardDirty = async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: '放弃未保存的修改？',
      message: '当前页面有还没保存的改动（包括刚添加的区块），切换后这些修改会丢失。',
      confirmText: '丢弃并切换',
      cancelText: '留在本页',
      tone: 'danger',
    });
  };

  const switchPage = async (nextId: string) => {
    if (nextId === pageId) return;
    if (!(await guardDirty())) return;
    setPageId(nextId);
  };

  const switchSite = async (nextId: string) => {
    if (nextId === siteId) return;
    if (!(await guardDirty())) return;
    setSiteId(nextId);
    setPageId('');
  };

  useEffect(() => {
    let cancelled = false;
    fetchBlocks(tenantId)
      .then(({ items }) => {
        if (!cancelled) setLibrary(items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((items) => {
        if (cancelled) return;
        setSites(items);
        // 只有当前选择不在新列表里时才换，否则切租户会把用户选中的站点冲掉
        setSiteId((prev) => (items.some((s) => s.site.id === prev) ? prev : (items[0]?.site.id ?? '')));
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => {
        if (!cancelled) setSitesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchCategories(tenantId, siteId)
      .then((items) => {
        if (!cancelled) setCategories(items);
      })
      .catch(() => {
        // 分类列表只用来给「按分类」这类字段填下拉选项。
        // 拉失败不该打断编辑，更不该弹错误盖住页面内容：
        // 表单会退回手填，客户照样能改标题和文案
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    /*
     * 切站点时先回到 null（"未知"）而不是 []（"没有"）：
     * 上一个站的资料列表对当前站是错的，而空数组会让客户看到
     * "资料库还没有文件"，于是跑去上传一份重复的文件。
     */
    setResources(null);
    fetchResources(tenantId, siteId)
      .then(({ items }) => {
        if (!cancelled) setResources(items);
      })
      .catch(() => {
        // 失败保持 null → 字段退回文本输入，已存的值仍然看得见、改得动
        if (!cancelled) setResources(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  useEffect(() => {
    if (!siteId) {
      // 还没选站点（或者一个站点都没有）：不该一直挂着"读取中"
      setPagesLoading(false);
      return;
    }
    let cancelled = false;
    setNotice(null);
    setPagesForSite(siteId);
    setPagesLoading(true);
    fetchPages(tenantId, siteId)
      .then((items) => {
        if (cancelled) return;
        setPages(items);
        setPageId((prev) => (items.some((p) => p.id === prev) ? prev : (items[0]?.id ?? '')));
        setError(null);
        setPagesLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setPagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  const loadPage = useCallback(
    async (id: string) => {
      if (!id) {
        setDetail(null);
        setBlocks([]);
        setDetailLoading(false);
        setDetailForPage('');
        return;
      }
      setDetailForPage(id);
      setDetailLoading(true);
      /*
       * 切页先把上一页的详情清掉。
       *
       * 留着的话，客户会在「/about」这个标题下看到、并且编辑到「/」的区块 ——
       * 因为保存用的是 `detail.id`，改完其实写回了上一页，而界面上没有任何
       * 异常提示。宁可中间露一段骨架，也不要让人改错页。
       */
      setDetail(null);
      setBlocks([]);
      try {
        const page = await fetchPage(tenantId, id);
        setDetail(page);
        setBlocks(page.blocks);
        setDirty(false);
        setError(null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setDetailLoading(false);
      }
    },
    [tenantId],
  );

  useEffect(() => {
    void loadPage(pageId);
  }, [pageId, loadPage]);

  /**
   * 换页面就重置两件视图状态：框在预览里的虚线、展开的区块。
   * 都属于"这一页的临时视角"，跟着页面走，不跟着会话走。
   */
  useEffect(() => {
    setActive(null);
    setExpanded(new Set());
  }, [pageId]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** 只把这一段设为展开，其余保持原样 —— 加完新区块要用它把新块顶开 */
  const reveal = (id: string) => {
    setExpanded((prev) => new Set(prev).add(id));
    setRevealTarget(id);
  };

  /**
   * 新加的区块排在列表末尾被顶开之后，还得让客户看见它。
   *
   * 默认全部收起之后这件事更要紧：原来加完就摊在眼前，现在只是"末尾多了一行
   * 标题"，屏幕外的话看起来就像"点了添加什么也没发生"。等这一帧渲染完再滚，
   * 滚到之后立刻清掉目标，否则后续任何一次重渲染都会把它再滚一遍，
   * 客户正往上翻的时候会被一直往下拽。
   */
  useEffect(() => {
    if (!revealTarget) return;
    const el = document.querySelector(`[data-block-anchor="${revealTarget}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    setRevealTarget(null);
  }, [revealTarget]);

  const current = useMemo(() => pages.find((p) => p.id === pageId), [pages, pageId]);
  /**
   * 列表与详情各自的真实状态。
   *
   * 不用 `pages.length === 0` 直接判空：初始值也是 `[]`，慢网下会把"还没读到"
   * 说成"你没有页面"。判断顺序见 `lib/data-phase.ts`。
   */
  const listPhase = loadPhaseOf({ loading: pagesLoading, error });
  /**
   * 详情也用同一个 `error`：详情读失败时 `detail` 是 null、`blocks` 是空数组，
   * 不带上这个错误就会说"这一页还没有区块"—— 而客户其实是没读到，一刷新就回来。
   */
  const detailPhase = loadPhaseOf({ loading: detailLoading, error });
  /**
   * 整块内容是否还在读（站点 → 页面列表 → 这一页的区块，三段接力）。
   *
   * 三段里任意一段没回来，正文区都只写一句「内容加载中…」：
   * 这段时间里**任何**空态文案都是假的 —— 站点还没读到就说不出"这个站点没有页面"，
   * 页面列表没读到就说不出口"这一页没有区块"。之前分开判，慢网下会先闪两段
   * 空态（还带两个主按钮），数据回来又整片换掉，看起来像加载失败重试了一次。
   *
   * `pagesForSite` / `detailForPage` 两处比对补的是"选中值已变、请求还没发出"
   * 的那一帧，光靠两个 loading 布尔值盖不住（它们是在 effect 里才翻的）。
   */
  const listLoading =
    sitesLoading || pagesLoading || (siteId !== '' && pagesForSite !== siteId);
  const contentLoading =
    listLoading || detailLoading || (pageId !== '' && detailForPage !== pageId);
  /** 空态只在 ready 之后成立，见 `lib/load-phase.ts` 里那段"为什么只有三态" */
  const listEmpty = !contentLoading && listPhase === 'ready' && pages.length === 0;
  /**
   * 这一页有没有区块。同样要排除"还在读"：`detail`/`blocks` 的初值也是空的，
   * 不排除的话站点还没回来时就会显示"这一页还没有区块"。
   */
  const blocksEmpty = !contentLoading && detailPhase === 'ready' && blocks.length === 0;
  /**
   * 页面按语言分组。
   *
   * 改之前是一个平铺的下拉框：12 个页面 × 4 种语言 = 48 行同构的名字，
   * 客户要找"西语的关于我们"只能一行行读。分组之后，语言是一个显式的选择，
   * 一次只在 12 个页面里挑。
   *
   * 语言清单来自 `SiteContext.locales`（站点开通了哪些），不是从页面反推 ——
   * 反推的话"西语一页都没有"这个状态在界面上根本不会出现，而那正是客户
   * 最需要看到的状态（他开通了语言却看不到页面，会以为系统坏了）。
   */
  const localeList: Locale[] = useMemo(() => {
    const ctx = sites.find((item) => item.site.id === siteId);
    if (!ctx) return [];
    const source = ctx.tenant.defaultLocale;
    return [source, ...ctx.locales.filter((item) => item !== source)];
  }, [sites, siteId]);

  /** 当前编辑的页面属于哪个语言。没有页面时退回默认语言 */
  const activeLocale: Locale =
    current?.locale ?? localeList[0] ?? ('en' as Locale);

  const pagesOf = useCallback(
    (locale: Locale) => pages.filter((page) => page.locale === locale),
    [pages],
  );

  /**
   * 切语言时尽量落在**同一页**上。
   *
   * 客户正在改 `/about` 的英文版，切到西语还想改 `/about`；直接跳到"西语第一个页面"
   * 会让他以为切错了。西语没有这一页时才退回第一个，并把原因说清楚。
   */
  const switchLocale = async (locale: Locale) => {
    // 列表还没回来时点语言标签，会把"还没读到"说成"这个语言没有页面"
    if (listLoading) return;
    const group = pagesOf(locale);
    if (group.length === 0) {
      setNotice(
        `${locale.toUpperCase()} 还没有任何页面。可以去「多语言翻译」把这个语言翻出来，或在「新建页面」时选它。`,
      );
      return;
    }
    const samePath = current ? group.find((page) => page.path === current.path) : undefined;
    const next = samePath ?? group[0]!;
    if (next.id === pageId) return;
    if (!(await guardDirty())) return;
    setPageId(next.id);
    setNotice(
      samePath
        ? null
        : `这个语言还没有 ${current?.path ?? ''} 这一页，先打开 ${next.path}。`,
    );
  };


  /** 返回是否保存成功。「保存并预览」要据此决定还开不开预览 */
  const onSave = async (): Promise<boolean> => {
    if (!detail) return false;
    setBusy(true);
    setNotice(null);
    try {
      const saved = await saveBlocks(tenantId, detail.id, blocks);
      setDetail(saved);
      setBlocks(saved.blocks);
      setDirty(false);
      setDraftSyncedAt(null);
      /**
       * 正式保存之后草稿就该退休：内容已经在库里了，留着它只会让下次预览
       * 打开时先闪一帧旧内容。失败不影响保存本身，靠 TTL 兜底。
       */
      void discardPreviewDraft(tenantId, detail.id).catch(() => {});
      setNotice(
        previewOpen
          ? `已保存 ${saved.blocks.length} 个区块，右侧预览正在刷新。`
          : `已保存 ${saved.blocks.length} 个区块。点「预览」可以立刻看到效果。`,
      );
      // 预览面板开着的时候"保存完还要自己点刷新"是最没必要的动作
      if (previewOpen) setPreviewReload((n) => n + 1);
      setPages((prev) =>
        prev.map((p) => (p.id === saved.id ? { ...p, blockCount: saved.blocks.length } : p)),
      );
      return true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * 预览联动：把未保存的改动同步成草稿，让右侧跟着变（P1-17 实时化）。
   *
   * 之前预览只读**已保存**内容，于是"勾掉一个区块的显示"在右边毫无反应 ——
   * 而保存是直接上线的，不能为了看一眼就保存。草稿把这两件事拆开：
   * 存草稿只影响预览，访客看到的仍是已保存的版本。
   *
   * 三处细节：
   *   · **防抖**：每敲一个字就整页重载 iframe，会闪得像坏了；
   *   · **序号**：并发 PUT 可能乱序返回，只认最后一次，否则预览会停在上一版
   *     而界面写着「已同步」；
   *   · **没有改动时主动清掉草稿**：上次改了没保存就关掉预览，草稿会留在库里，
   *     下次打开预览先看到那份旧内容 —— 客户会以为自己改的东西还在。
   */
  useEffect(() => {
    if (!previewOpen || !detail) return;

    if (!dirty) {
      void discardPreviewDraft(tenantId, detail.id).catch(() => {});
      setDraftSyncedAt(null);
      return;
    }

    const pageId = detail.id;
    const timer = window.setTimeout(() => {
      const seq = ++draftSeq.current;
      savePreviewDraft(tenantId, pageId, blocks)
        .then((saved) => {
          if (seq !== draftSeq.current) return;
          setDraftSyncedAt(new Date(saved.savedAt));
          setPreviewReload((n) => n + 1);
        })
        .catch((err: unknown) => {
          if (seq !== draftSeq.current) return;
          /**
           * 草稿失败**不拦编辑**：客户还能继续改、继续保存。
           * 但要说清楚是"预览跟不上了"，否则他会以为右侧就是最终效果。
           */
          setError(
            `预览没能跟上刚才的改动：${err instanceof Error ? err.message : String(err)}。改动仍在本地，点「保存修改」后才会生效。`,
          );
        });
    }, PREVIEW_DRAFT_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [previewOpen, dirty, blocks, detail, tenantId]);

  /**
   * 打开预览（P1-17）。
   *
   * 三个细节都是有原因的：
   *   ① 脏状态先保存：预览显示的是服务端已保存的内容，不保存就预览，客户会
   *      在"我明明改了"和"预览里没变"之间反复怀疑自己。
   *   ② 窗口在点击的**同一帧**就打开：await 之后浏览器已经把那一次点击算作
   *      过期手势，window.open 会被拦截，表现为"点了没反应"。
   *   ③ 打开的空白页要主动断开 opener：预览页是客户的站点，不能让它反过来
   *      操纵控制台窗口。
   */
  const openPreview = async () => {
    if (!detail) return;
    const site = sites.find((s) => s.site.id === siteId);
    if (!site) return;
    const host = site.site.primaryHost;
    if (!host) {
      setError('这个站点还没有可访问的域名，无法预览。先到「域名绑定」页绑定一个域名。');
      return;
    }

    /**
     * 先开一个空白页，等令牌回来再让它跳过去。
     *
     * 这一整段都包在 try 里：某些环境（启用 COOP、被策略收紧的自动化浏览器）
     * 下给 `opener` 赋值或读新窗口的 document 会直接抛 SecurityError。
     * 抛出去的话整个点击处理器就中断了 —— 表现为"点预览什么都没发生"，
     * 而控制台里连一条错误都没有，无法自查。这两行只是观感，失败不影响功能。
     */
    const tab = openBlankTab();
    if (tab) {
      try {
        tab.opener = null;
        tab.document.title = '正在准备预览…';
      } catch {
        // 拿不到句柄就算了，后面照样用它跳转
      }
    }

    try {
      if (dirty) {
        const proceed = await confirm({
          title: '先保存，再预览',
          message: '预览显示的是已保存到服务器的内容。当前还有没保存的改动，继续会先把它们保存，然后再打开预览。',
          confirmText: '保存并预览',
          cancelText: '取消',
        });
        if (!proceed) {
          tab?.close();
          return;
        }
        if (!(await onSave())) {
          // 保存失败就不开预览：打开只会看到旧内容，客户会以为保存成功了
          tab?.close();
          return;
        }
      }

      const { token } = await createPreviewToken(tenantId, detail.id);
      const path = withLocalePrefix(detail.path, detail.locale, site.tenant.defaultLocale);
      const url =
        `${previewOrigin(host)}/api/preview` +
        `?token=${encodeURIComponent(token)}&to=${encodeURIComponent(path)}`;
      if (tab) {
        try {
          tab.location.replace(url);
        } catch {
          // 句柄失效就再开一次；再失败才退化成当前页跳转
          if (!window.open(url, '_blank')) window.location.href = url;
        }
      } else {
        window.location.href = url;
      }
    } catch (err: unknown) {
      tab?.close();
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const togglePublish = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const next = detail.status === 'published' ? 'draft' : 'published';
      const saved = await updatePage(tenantId, detail.id, { status: next });
      setDetail(saved);
      setPages((prev) => prev.map((p) => (p.id === saved.id ? { ...p, status: saved.status } : p)));
      setNotice(next === 'draft' ? '已转为草稿：该页面现在对外返回 404。' : '已发布。');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 新建页面。
   *
   * 服务端会给已开通的**每一种语言各建一版**（草稿），所以这里建完要重新拉列表，
   * 不能只把返回值塞进 pages —— 页面下拉是按 path + locale 排的，少几次刷新
   * 客户就会在列表里找不到刚建的那页。
   */
  const onCreatePage = async () => {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createPages(tenantId, {
        siteId,
        title: newTitle.trim(),
        path: newPath.trim(),
        ...(newCopyFrom ? { copyFromPageId: newCopyFrom } : {}),
      });
      setPages(await fetchPages(tenantId, siteId));
      const first = created[0];
      if (first) setPageId(first.id);
      setNewPageOpen(false);
      setNewTitle('');
      setNewPath('');
      setNewCopyFrom('');
      setNotice(
        `已建好 ${created.length} 个语言版本，都是草稿。加完内容点「发布」，才能在菜单的「页面」里选到它。`,
      );
    } catch (err: unknown) {
      // 抽屉不关：客户改个地址就能重试，关掉等于让他重新填
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 删除当前页。
   *
   * 只对自建页面（`kind === 'page'`）开放：首页、产品页、联系页是主题版式的落点，
   * 服务端也会拒（`page_locked`）。前端先禁用按钮，省得客户点了才看到报错。
   *
   * 删的是**当前语言这一行**。文案里必须写明，否则客户会以为"这一个语言版本
   * 就是整个页面"，删完发现另一门语言还在。
   */
  const onDeletePage = async () => {
    if (!detail) return;
    const ok = await confirm({
      title: '删除这一页？',
      message: `将删除 ${detail.path}（${detail.locale.toUpperCase()}）这一页及它的全部内容，无法恢复。其他语言版本不受影响。`,
      confirmText: '删除',
      cancelText: '取消',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await deletePage(tenantId, detail.id);
      const items = await fetchPages(tenantId, siteId);
      setPages(items);
      setPageId(items[0]?.id ?? '');
      setNotice('已删除。');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 分类树 → 下拉选项。
   *
   * value 用**主 slug**（`node.slug`）：它是稳定标识，不随语言变化，
   * 也和前台商品视图里的 `categoryBaseSlug` 对得上。label 带上父级路径，
   * 因为"筒灯"这种名字在两个父分类下各有一个是常事，只显示名字等于让人猜。
   */
  const choices = useMemo<FieldChoices>(() => {
    const flat: Array<{ value: string; label: string }> = [];
    const walk = (nodes: CategoryNode[], prefix: string[]) => {
      for (const node of nodes) {
        const name =
          node.translations.zh?.name ?? node.translations.en?.name ?? node.slug;
        flat.push({ value: node.slug, label: [...prefix, name].join(' / ') });
        if (node.children.length > 0) walk(node.children, [...prefix, name]);
      }
    };
    walk(categories, []);
    return {
      category: flat,
      /*
       * 资料的下拉标签带上文件名与大小。
       *
       * 不带的原因只有一个：标签本来是客户自己起的（「2026 目录」），
       * 但同一个名字下常常传过好几版，也可能同名传了 PDF 和 ZIP ——
       * 只显示标签就分不出哪个是哪个。已下架的资料仍然列出来
       * （客户可能正想把某页换回旧的），但标出来，避免"选了却打不开"。
       */
      resource:
        resources === null
          ? undefined
          : resources.map((item) => ({
              value: item.id,
              label: `${item.name} · ${item.fileName} · ${formatFileSize(item.bytes)}${
                item.enabled ? '' : '（已下架）'
              }`,
            })),
      /*
       * 媒体库上下文。没有站点时不给：图片字段会退化成纯地址输入，
       * 而不是弹出一个"当前站点为空"的选择器。
       */
      ...(siteId ? { media: { tenantId, siteId, canGenerateArt } } : {}),
    };
  }, [categories, resources, siteId, tenantId, canGenerateArt]);

  /** 区块类型 → 中文名。列表里显示 `hero` 这种标识符对客户没有意义 */
  const titleOf = useMemo(
    () => new Map(library.map((item) => [item.type, item.title.zh])),
    [library],
  );

  /** 区块类型 → 表单字段描述。没有描述符的块退回通用 JSON 渲染 */
  const fieldsOf = useMemo(
    () => new Map(library.filter((item) => item.fields?.length).map((item) => [item.type, item.fields!])),
    [library],
  );

  const usedOf = (type: string) => blocks.filter((b) => b.type === type).length;

  /**
   * 光标进到某一段里 → 告诉预览"看这里"。
   *
   * 同一个块重复触发时返回原对象：`setState` 拿到同一个引用不会重渲染，
   * 也就不会重复发消息。客户在一个区块里连点三个输入框，预览只该定位一次。
   *
   * 放在 `titleOf` 之后：标签要用区块的中文名，而那个映射是上面几个 useMemo
   * 算出来的 —— 写在前面对 JS 没问题（调用发生在渲染之后），但读代码的人
   * 会以为它可能拿到空 Map。
   */
  const focusBlock = (block: StoredBlock) => {
    setActive((prev) => {
      if (prev?.blockId === block.id) return prev;
      const ids = highlightIdsOf(block);
      if (ids.length === 0) return null;
      return { blockId: block.id, target: { ids, label: titleOf.get(block.type) ?? block.type } };
    });
  };

  /**
   * 首屏是**换**不是**加**。
   *
   * 一页只能有一个第一屏，渲染层也是这么做的：客户加的第二个首屏块不会被
   * 渲染（记进 degraded），因为它顶上不了一个位置。所以在编辑器里就不该
   * 让客户加出两个来 —— 等前台空白了再回头查是哪的问题，成本高得多。
   *
   * 判据用区块库给的 `category`，不是写死 `hero-` 前缀：分组是协议里
   * 的稳定标识，而组件名以后还可能加新的构图。
   */
  const categoryOf = useMemo(
    () => new Map(library.map((item) => [item.type, item.category])),
    [library],
  );
  const isHeroBlock = (type: string) => categoryOf.get(type) === 'hero';
  const heroIndex = blocks.findIndex((b) => isHeroBlock(b.type));
  const heroCount = blocks.filter((b) => isHeroBlock(b.type)).length;

  /**
   * 上移 / 下移。
   *
   * 首屏**不能动**，也不能被别的段挤下去 —— 页面上它永远画在最上面
   * （`renderPage` 把首屏单独放进 `.th-hero-region`，主体排在它后面）。
   * 列表里允许把它拖到第五行，就是在制造"左边看着对、右边不对"：
   * 客户拖完保存、前台纹丝不动，只能得出"排序按钮时灵时不灵"。
   * 与其让渲染层去追列表，不如让列表承认首屏的位置是固定的。
   */
  const canMove = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return false;
    const moving = blocks[index];
    const crossing = blocks[target];
    if (!moving || !crossing) return false;
    return !isHeroBlock(moving.type) && !isHeroBlock(crossing.type);
  };

  const move = (index: number, delta: number) => {
    if (!canMove(index, delta)) return;
    const next = [...blocks];
    const target = index + delta;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    setBlocks(next);
    setDirty(true);
  };

  const addBlock = (item: BlockLibraryItem) => {
    if (!detail) return;
    /**
     * 已有首屏时，点首屏分类里的任何一张 = 就地换掉它，而不是再加一个。
     * 保留原 id 与位置：客户在首屏上面/下面排好的顺序不应该因为换了个构图而乱。
     */
    if (item.category === 'hero' && heroIndex >= 0) {
      const next = [...blocks];
      const current = next[heroIndex];
      if (!current) return;
      next[heroIndex] = {
        ...current,
        type: item.type,
        version: item.version,
        // 换了组件，上一个组件的字段全是废字段，从新组件默认值重来
        props: structuredClone(item.defaults),
      };
      setBlocks(next);
      setDirty(true);
      setError(null);
      /*
       * 换的是"位置不动的那一个"，它可能正收着。展开它再看效果，
       * 否则客户换了个构图，眼前唯一的反应只是标题那行字变了。
       */
      reveal(current.id);
      setNotice(`首屏已换成「${item.title.zh}」，点右上角「保存修改」后才会生效。`);
      setPickerOpen(false);
      return;
    }
    const used = usedOf(item.type);
    if (item.maxPerPage && used >= item.maxPerPage) {
      setError(`「${item.title.zh}」单页最多 ${item.maxPerPage} 个，当前已有 ${used} 个。`);
      return;
    }
    if (blocks.length >= MAX_BLOCKS_PER_PAGE) {
      setError(`单页最多 ${MAX_BLOCKS_PER_PAGE} 个区块，请先删掉几个再加。`);
      return;
    }
    const next: StoredBlock = {
      id: newBlockId(),
      type: item.type,
      version: item.version,
      // 深拷贝：defaults 来接口响应，浅拷贝会让同类型的两个块共享同一份数组/对象
      props: structuredClone(item.defaults),
      visible: true,
    };
    /**
     * 首屏插到最前面 —— 它无论如何都画在页首。
     *
     * 一路追加到末尾的话（早先的写法），客户刚加完的首屏在列表最后一行、
     * 在页面上却是第一屏：同一份数据两个位置，后面每次排序都会踩到这个错位。
     */
    setBlocks((prev) => (item.category === 'hero' ? [next, ...prev] : [...prev, next]));
    setDirty(true);
    setError(null);
    // 默认全收起，新加的这块必须自己顶开 + 滚进来，否则等于"加了但看不见"
    reveal(next.id);
    setNotice(`已添加「${item.title.zh}」，点右上角「保存修改」后才会生效。`);
    setPickerOpen(false);
  };

  /**
   * 存量数据里的两个首屏（编辑器加这条规则之前加的）。
   *
   * 不自动删 —— 删哪个是客户的决定。但必须当面说清楚"前面那个在渲染、后面那个不在"，
   * 否则他会以为页面坏了。
   */
  const keepFirstHero = () => {
    let seen = false;
    setBlocks((prev) =>
      prev.filter((block) => {
        if (!isHeroBlock(block.type)) return true;
        if (seen) return false;
        seen = true;
        return true;
      }),
    );
    setDirty(true);
    setNotice('已删掉多余的首屏区块，点右上角「保存修改」后生效。');
  };

  /**
   * 一个都没展开（= 默认状态）时，顶部那颗按钮叫「全部展开」，
   * 否则叫「全部收起」—— 同一个位置不做两件事。
   */
  const noneExpanded = blocks.length > 0 && blocks.every((b) => !expanded.has(b.id));
  /** 有多少段被取消勾选了「显示」。收起状态下客户看不见勾选框，只能靠这行字知道 */
  const hiddenCount = blocks.filter((b) => b.visible === false).length;
  /**
   * 交给预览面板的高亮目标。预览关着就发 null（面板不在，也没必要留状态）。
   * 注意这里只是**取引用**，不构造新对象 —— 见 `active` 的注释。
   */
  const highlightTarget = previewOpen ? (active?.target ?? null) : null;

  return (
    <section className="stack">
      <div className="tablebar">
        <label className="picker">
          <span>站点</span>
          <select
            className="field"
            value={siteId}
            onChange={(e) => void switchSite(e.target.value)}
          >
            {/* 站点是第一个要读的东西，空下拉框在它回来之前等于"你没有站点" */}
            {sitesLoading ? (
              <option value="" disabled>
                正在读取…
              </option>
            ) : null}
            {!sitesLoading && sites.length === 0 ? (
              <option value="" disabled>
                还没有站点
              </option>
            ) : null}
            {sites.map((s) => (
              <option key={s.site.id} value={s.site.id}>
                {s.site.name}
              </option>
            ))}
          </select>
        </label>
        {/**
         * 语言标签 + 只属于这门语言的页面下拉。
         *
         * 两种信息分开摆：语言是"我在编辑哪个版本"，页面是"哪一个页面"。
         * 之前挤在一个下拉里，48 个选项长得一模一样。
         */}
        {localeList.length > 1 ? (
          <div className="locale-tabs">
            {localeList.map((item) => {
              const count = pagesOf(item).length;
              return (
                <button
                  key={item}
                  type="button"
                  className={`locale-tab${item === activeLocale ? ' is-active' : ''}`}
                  onClick={() => void switchLocale(item)}
                  title={
                    listLoading
                      ? `正在读取${LOCALE_LABEL[item]}的页面…`
                      : count === 0
                        ? `${LOCALE_LABEL[item]} 还没有页面`
                        : `${LOCALE_LABEL[item]} 有 ${count} 个页面`
                  }
                >
                  {LOCALE_LABEL[item]}
                  {/* 数字用弱化的样式：它是"这个语言有几页"，不是徽章也不是提醒。
                      读取中不打数字：`0` 配着 is-zero 的浅色，看起来就是"这个语言没内容" */}
                  {listLoading ? null : (
                    <span className={`locale-tab__count${count === 0 ? ' is-zero' : ''}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : null}
        <label className="picker">
          <span>页面</span>
          <select
            className="field"
            value={pageId}
            onChange={(e) => void switchPage(e.target.value)}
          >
            {/* 空下拉框在慢网下等于"这个语言一页都没有"。占位项要说清是哪一种空 */}
            {listLoading ? (
              <option value="" disabled>
                正在读取…
              </option>
            ) : null}
            {!listLoading && pagesOf(activeLocale).length === 0 ? (
              <option value="" disabled>
                还没有页面
              </option>
            ) : null}
            {pagesOf(activeLocale).map((p) => (
              <option key={p.id} value={p.id}>
                {p.path} · {p.status === 'published' ? '已发布' : '草稿'}
              </option>
            ))}
          </select>
        </label>
        {current ? (
          <Badge tone={current.status === 'published' ? 'success' : 'warn'}>
            {current.status === 'published' ? '已发布' : '草稿'}
          </Badge>
        ) : null}
        <div className="tablebar__right">
          <Button variant="ghost" onClick={() => setNewPageOpen(true)} disabled={busy || !siteId}>
            新建页面
          </Button>
          <Button variant="ghost" onClick={() => setPickerOpen(true)} disabled={busy || !detail}>
            添加区块
          </Button>
          <Button
            variant={previewOpen ? 'primary' : 'ghost'}
            onClick={() => setPreviewOpen((open) => !open)}
            disabled={busy || !detail}
            aria-pressed={previewOpen}
          >
            {previewOpen ? '收起预览' : '预览'}
          </Button>
          <Button variant="ghost" onClick={togglePublish} disabled={busy || !detail}>
            {detail?.status === 'published' ? '转为草稿' : '发布'}
          </Button>
          {/*
            只对自建页面出现：首页、产品页、联系页在库里 kind 同样是 'page'，
            区别就在路径（服务端也按这个判，见 THEME_FIXED_PATHS）。
            前端先隐藏，省得客户点了才看到报错。
          */}
          {detail?.kind === 'page' &&
          !(THEME_FIXED_PATHS as readonly string[]).includes(detail.path) ? (
            <Button
              variant="ghost"
              onClick={onDeletePage}
              disabled={busy}
              title="只删当前语言这一版，其他语言不受影响"
            >
              删除
            </Button>
          ) : null}
          <Button variant="primary" onClick={onSave} disabled={busy || !dirty}>
            {dirty ? '保存修改' : '已保存'}
          </Button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice">{notice}</p> : null}

      {heroCount > 1 ? (
        <div className="notice">
          这一页有 {heroCount} 个首屏区块，前台只用第一个，其余不会显示。
          <Button variant="ghost" size="sm" onClick={keepFirstHero}>
            只保留第一个
          </Button>
        </div>
      ) : null}

      {/*
        编辑区与预览面板并排。

        用「左边一列表单 + 右边一列按钮」而不是一个浮层：浮层会盖住正在编辑的
        那几行，客户要么关掉预览、要么滚到别处去改，等于没解决"改一次切两个窗口"。

        刚进来还没有任何页面时**不摆右列**：那时预览面板无从渲染（它要一页的地址），
        留着就是一整块空白灰底，跟左边那句「内容加载中…」凑在一起像页面加载坏了。
        已经有页面（哪怕区块还在读）就留列，这样切页面时左右宽度不会跳一下。
      */}
      <div className={`editor-split${previewOpen && (detail || pageId) ? ' is-split' : ''}`}>
        <div className="stack">
          {/*
            三种"看起来都是空的"要分开说：正在读、读失败、真的没有。

            读的时候只写一句「内容加载中…」：站点、页面列表、区块是三次接力请求，
            各画一段骨架的话，慢网下一进页面先闪过三块灰条、再闪过两段空态卡片，
            客户以为加载失败重试了一次。一句就够，也说明现在到底在等什么。
          */}
          {contentLoading ? (
            <p className="loading-line" role="status" aria-live="polite">
              内容加载中…
            </p>
          ) : null}

          {listEmpty ? (
            <Card flush>
              <div className="blocklist-empty">
                <p>这个站点还没有页面。</p>
                <p className="muted">
                  如果你在前台看到已经有内容，那是内置的演示页，不在这里编辑。新建一页就能开始写自己的。
                </p>
                <Button variant="primary" onClick={() => setNewPageOpen(true)} disabled={!siteId}>
                  新建第一个页面
                </Button>
              </div>
            </Card>
          ) : null}

          {blocksEmpty ? (
            <Card flush>
              <div className="blocklist-empty">
                <p>这一页还没有区块。</p>
                <Button variant="primary" onClick={() => setPickerOpen(true)}>
                  添加第一个区块
                </Button>
              </div>
            </Card>
          ) : null}

          {blocks.length > 0 ? (
            <div className="blocklist-bar">
              <span className="muted">
                共 {blocks.length} 个区块
                {hiddenCount > 0 ? ` · ${hiddenCount} 个已取消勾选「显示」` : ''}
                {/* 默认全收起，第一眼看到的是一列标题，得告诉新手怎么打开 */}
                {noneExpanded ? ' · 点区块左侧的三角展开' : ''}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setExpanded(noneExpanded ? new Set(blocks.map((b) => b.id)) : new Set())
                }
              >
                {noneExpanded ? '全部展开' : '全部收起'}
              </Button>
            </div>
          ) : null}

          {blocks.map((block, index) => {
            const isCollapsed = !expanded.has(block.id);
            /*
             * 收起时的一行摘要 —— 只在这一段藏着时算。
             *
             * 展开时字段就在下面，摘要纯属重复，还会把标题行撑成两行。
             * 收起的列表才是"目录"，目录里每一条得能认出来是哪一段。
             */
            const summary = isCollapsed ? blockSummary(block.props, fieldsOf.get(block.type)) : '';
            return (
              <div
                key={block.id}
                className="block-item"
                /* 新加的区块要滚进视口，用它定位；见 reveal() */
                data-block-anchor={block.id}
                /*
                 * 焦点落进来 = 客户在看这一段，右侧预览据此把它框出来。
                 *
                 * 用 `onFocus`（React 的焦点事件是 focusin，会冒泡）而不是给每个
                 * 输入框单独挂：字段是遍历生成的，逐个挂要改的地方太多，漏一个
                 * 就有一种字段"定位不到"。`onPointerDown` 补的是点区块标题这种
                 * 不会产生焦点的动作。
                 */
                onFocus={() => focusBlock(block)}
                onPointerDown={() => focusBlock(block)}
              >
                <Card className={`block-card${isCollapsed ? ' is-collapsed' : ''}`}>
                  <div className="block-head">
                    {/*
                     * 标题杆整块是开关。右侧那排按钮是「对这一段的动作」，
                     * 把收放混进去会被当成"又一个操作"；而客户的意图是
                     * "这一段先放起来"，所以开关必须贴在标题上。
                     *
                     * 三角画成一颗带底色的圆角块，而不是一个裸字符：裸 ▸
                     * 在浅底上和旁边的正文一样灰，新手根本不会想到它是按钮
                     * （这是上线后收到的实际反馈）。展开/收起两态用"实心蓝"
                     * 与"淡蓝"区分，最需要被看见的时刻（内容藏着）最显眼。
                     */}
                    <button
                      type="button"
                      className="block-head__toggle"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleExpanded(block.id)}
                      title={isCollapsed ? '展开这一段' : '收起这一段'}
                    >
                      <span className="block-head__title">
                        <BlockChevron />
                        <strong>{titleOf.get(block.type) ?? block.type}</strong>
                        <span className="block-head__type">{block.type}</span>
                        <span className="muted">v{block.version}</span>
                        {/* 收起之后看不到勾选框，这一行小字是唯一的提示 */}
                        {block.visible ? null : <span className="block-head__off">已隐藏</span>}
                      </span>
                      {/*
                       * 摘要只在收起时给：目录里光有「常见问题」四个字，
                       * 客户还是不知道这一段写的是哪几句话 —— 尤其是同一页
                       * 有两三个富文本段落时。这里显示他自己写的那句标题。
                       */}
                      {summary ? (
                        <span className="block-head__summary" title={summary}>
                          {summary}
                        </span>
                      ) : null}
                    </button>
                    <div className="block-head__actions">
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={block.visible}
                          onChange={(e) => {
                            const next = [...blocks];
                            next[index] = { ...block, visible: e.target.checked };
                            setBlocks(next);
                            setDirty(true);
                          }}
                        />
                        <span>显示</span>
                      </label>
                      <Button
                        variant="ghost"
                        onClick={() => move(index, -1)}
                        disabled={!canMove(index, -1)}
                      >
                        上移
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => move(index, 1)}
                        disabled={!canMove(index, 1)}
                      >
                        下移
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setBlocks(blocks.filter((_, i) => i !== index));
                          setDirty(true);
                          // 删掉的正是预览框着的那一段：框该跟着消失，而不是继续指着空气
                          if (active?.blockId === block.id) setActive(null);
                        }}
                      >
                        删除
                      </Button>
                    </div>
                  </div>

                  {isCollapsed
                    ? null
                    : (() => {
                        const fields = fieldsOf.get(block.type);
                        const applyProps = (next: Record<string, unknown>) => {
                          const updated = [...blocks];
                          updated[index] = { ...block, props: next };
                          setBlocks(updated);
                          setDirty(true);
                        };
                        if (fields) {
                          return (
                            <BlockPropsEditor
                              fields={fields}
                              value={block.props}
                              onChange={applyProps}
                              choices={choices}
                            />
                          );
                        }
                        return (
                          <div className="fields">
                            <MissingFieldsNotice type={block.type} />
                            <PropFields
                              value={block.props}
                              onChange={(next) => applyProps(next as Record<string, unknown>)}
                            />
                          </div>
                        );
                      })()}
                </Card>
              </div>
            );
          })}
        </div>

        {/*
          只在 detail 到手后才挂预览面板。
          面板在拿不到 host 时会显示"这个站点还没有可访问的域名" —— 而首帧的
          host 本来就是 null（站点列表还没回来），于是默认展开时那行错误的提示
          会先闪一下再消失。等 detail 意味着站点列表早已就位（detail 的请求由
          siteId 触发），没域名这句才是真话。
        */}
        {previewOpen && detail ? (
          <PreviewPane
            tenantId={tenantId}
            pageId={detail.id}
            path={withLocalePrefix(
              detail.path,
              detail.locale,
              sites.find((s) => s.site.id === siteId)?.tenant.defaultLocale ?? detail.locale,
            )}
            host={sites.find((s) => s.site.id === siteId)?.site.primaryHost ?? null}
            reloadSignal={previewReload}
            dirty={dirty}
            draftAt={draftSyncedAt}
            highlight={highlightTarget}
            onClose={() => setPreviewOpen(false)}
            onOpenTab={() => void openPreview()}
          />
        ) : null}
      </div>

      <Drawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="添加区块"
        description="选中的区块会加到当前页面末尾。加完还要点「保存修改」，否则刷新就没了。"
        footer={
          <Button variant="ghost" onClick={() => setPickerOpen(false)}>
            关闭
          </Button>
        }
      >
        {library.length === 0 ? (
          <p className="muted">区块库还没加载出来，稍后再试。</p>
        ) : (
          CATEGORY_ORDER.map((category) => {
            const items = library.filter((item) => item.category === category);
            if (items.length === 0) return null;
            return (
              <div key={category} className="blockpicker__group">
                <h3 className="blockpicker__title">{CATEGORY_LABEL[category]}</h3>
                <ul className="blockpicker__list">
                  {items.map((item) => {
                    const used = usedOf(item.type);
                    const full = Boolean(item.maxPerPage && used >= item.maxPerPage);
                    /** 首屏已有 → 这一个按钮是「替换」，不受 maxPerPage 限制 */
                    const swapHero = item.category === 'hero' && heroIndex >= 0;
                    return (
                      <li key={item.type} className="blockpicker__item">
                        <div className="blockpicker__text">
                          <div className="blockpicker__name">
                            <strong>{item.title.zh}</strong>
                            <span className="blockpicker__type">{item.type}</span>
                          </div>
                          <p className="muted">
                            {swapHero
                              ? '替换当前首屏，位置与顺序不变'
                              : item.maxPerPage
                                ? `单页最多 ${item.maxPerPage} 个，已用 ${used} 个`
                                : `已用 ${used} 个`}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addBlock(item)}
                          disabled={(!swapHero && full) || blocks.length >= MAX_BLOCKS_PER_PAGE}
                          title={!swapHero && full ? `「${item.title.zh}」已经加到上限` : undefined}
                        >
                          {swapHero ? '替换首屏' : full ? '已满' : '添加'}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </Drawer>

      {/*
        新建页面。表单只有三项，所以用抽屉而不是整页向导：
        客户在这页上通常一边看着现有页面一边想"还缺一页"，
        跳走再回来会丢掉这个上下文。
      */}
      <Drawer
        open={newPageOpen}
        onClose={() => setNewPageOpen(false)}
        title="新建页面"
        description="会给这个站已开通的每种语言各建一版，先存成草稿。建好到这里加段落，发布后才能挂进菜单。"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNewPageOpen(false)}>
              取消
            </Button>
            <Button
              variant="primary"
              onClick={() => void onCreatePage()}
              disabled={busy || newTitle.trim() === '' || newPath.trim() === ''}
            >
              {busy ? '创建中…' : '创建'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <label className="field">
            <span>页面标题</span>
            <input
              className="field"
              value={newTitle}
              placeholder="关于我们"
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <span className="muted">显示在浏览器标签与菜单里，之后可以改。</span>
          </label>

          <label className="field">
            <span>页面地址</span>
            <input
              className="field"
              value={newPath}
              placeholder="about-us"
              onChange={(e) => setNewPath(e.target.value)}
            />
            {/*
              一句白话把规则说完：客户最常踩的两个坑是填中文、填完整网址。
              服务端仍会校一遍，这里只是把错误提前到打字的时候。
            */}
            <span className="muted">
              小写字母、数字和连字符，例如 about-us 或 about/team。不能与商品、分类的地址冲突。
            </span>
          </label>

          <label className="field">
            <span>起始内容</span>
            <select
              className="field"
              value={newCopyFrom}
              onChange={(e) => setNewCopyFrom(e.target.value)}
            >
              <option value="">主题的内容页版式（推荐）</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  复制 {p.path} · {p.locale.toUpperCase()}
                </option>
              ))}
            </select>
            <span className="muted">
              选「复制」会把那一页的段落与文案一起抄过来，适合"产品 A 详情"这类同构页。
            </span>
          </label>
        </div>
      </Drawer>

      {confirmDialog}
    </section>
  );
}

// ── props 遍历 ────────────────────────────────────────────────────────

type Json = unknown;

/** 只做值替换，返回新对象，不改原对象 —— 直接改会让 React 认为没变化 */
function replaceAt(node: Json, path: (string | number)[], value: Json): Json {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(node)) {
    const copy = [...node];
    copy[Number(head)] = replaceAt(copy[Number(head)], rest, value);
    return copy;
  }
  const copy = { ...(node as Record<string, Json>) };
  copy[String(head)] = replaceAt(copy[String(head)], rest, value);
  return copy;
}

function PropFields({
  value,
  onChange,
  path = [],
  label = '',
}: {
  value: Json;
  onChange: (next: Json) => void;
  path?: (string | number)[];
  label?: string;
}) {
  if (typeof value === 'string') {
    // 长文案用 textarea：客户填的是"首屏副标题"这类成句内容，
    // 单行 input 里改一段话是很难受的
    const long = value.length > 60;
    return (
      <label className="form-row">
        <span>{label || '值'}</span>
        {long ? (
          <textarea
            className="field"
            rows={3}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input className="field" value={value} onChange={(e) => onChange(e.target.value)} />
        )}
      </label>
    );
  }

  if (typeof value === 'number') {
    return (
      <label className="form-row">
        <span>{label || '值'}</span>
        <input
          className="field"
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </label>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <label className="form-row check">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span>{label || '开关'}</span>
      </label>
    );
  }

  if (value === null) {
    return (
      <label className="form-row">
        <span>{label || '值'}</span>
        <input
          className="field"
          placeholder="（空）输入内容后即写入"
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      </label>
    );
  }

  if (Array.isArray(value)) {
    return (
      <fieldset className="group">
        <legend>{label || '列表'}（{value.length}）</legend>
        {value.map((item, i) => (
          <PropFields
            key={i}
            value={item}
            path={[...path, i]}
            label={`[${i + 1}]`}
            onChange={(next) => onChange(replaceAt(value, [i], next))}
          />
        ))}
        <p className="muted">增删条目暂不支持，先在种子内容里保留足够的条目再编辑。</p>
      </fieldset>
    );
  }

  if (typeof value === 'object') {
    return (
      <fieldset className="group">
        <legend>{label || '分组'}</legend>
        {Object.entries(value as Record<string, Json>).map(([key, child]) => (
          <PropFields
            key={key}
            value={child}
            path={[...path, key]}
            label={key}
            onChange={(next) => onChange(replaceAt(value, [key], next))}
          />
        ))}
      </fieldset>
    );
  }

  return null;
}
